"use client";

import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type FC,
  type Ref,
} from "react";
import { formatNumberAsMoney, isEmpty } from "@huub-web/utils";

import {
  TextField,
  type TextFieldChangeHandler,
  type TextFieldProps,
} from "../TextField/TextField";
import {
  filterInvalidCharacters,
  calculateCursorPosition,
} from "./utils/index";

export type NumberFieldProps = Omit<
  TextFieldProps,
  "type" | "value" | "onChange"
> & {
  value?: number | string;
  onChange?: NumberFieldChangeHandler;
  decimals?: boolean;
  minimumFractionDigits?: number;
  formatThousandSeparator?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export type NumberFieldChangeHandler = (
  value: number | null,
  e: ChangeEvent<HTMLInputElement>,
) => void;

/**
 * NumberField with real-time formatting and cursor position preservation.
 * Formats numbers with thousand separators as you type while maintaining
 * the cursor position for seamless editing experience.
 */
export const NumberField: FC<NumberFieldProps> = ({
  value,
  onChange,
  align = "right",
  decimals = true,
  formatThousandSeparator = true,
  ref,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState<string>(() =>
    isEmpty(value)
      ? ""
      : formatThousandSeparator
        ? formatNumberAsMoney(value, "decimal")
        : String(value),
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number | null>(null);

  // Sync displayValue when value prop changes (e.g., from localStorage)
  useEffect(() => {
    // Only update if input is not focused (to avoid interfering with typing)
    if (document.activeElement !== inputRef.current) {
      const newDisplayValue = isEmpty(value)
        ? ""
        : formatThousandSeparator
          ? formatNumberAsMoney(value, "decimal")
          : String(value);
      setDisplayValue(newDisplayValue);
    }
  }, [value, formatThousandSeparator]);

  // Restore cursor position after formatting
  useEffect(() => {
    if (
      cursorPositionRef.current !== null &&
      inputRef.current &&
      document.activeElement === inputRef.current
    ) {
      inputRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current,
      );
      cursorPositionRef.current = null;
    }
  }, [displayValue]);

  const handleChange: TextFieldChangeHandler = (newValue, e) => {
    const input = e.target;
    const oldCursorPosition = input.selectionStart ?? newValue.length;

    const filteredValue = filterInvalidCharacters(newValue, decimals);
    const cleanedValue = filteredValue.replace(/\./g, "").replace(",", ".");

    // Format immediately if formatThousandSeparator is enabled
    let finalDisplayValue = filteredValue;
    if (formatThousandSeparator && filteredValue) {
      // Split into integer and decimal parts
      const parts = filteredValue.split(",");
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Add thousand separators to integer part
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ".",
      );

      // Combine back
      finalDisplayValue =
        decimalPart !== undefined
          ? `${formattedInteger},${decimalPart}`
          : formattedInteger;
    }

    // Calculate new cursor position
    const newCursorPosition = calculateCursorPosition(
      displayValue,
      finalDisplayValue,
      oldCursorPosition,
    );

    cursorPositionRef.current = newCursorPosition;
    setDisplayValue(finalDisplayValue);

    const numericValue = decimals
      ? parseFloat(cleanedValue)
      : parseInt(cleanedValue, 10);

    if (Number.isNaN(numericValue)) {
      onChange?.(filteredValue === "" ? null : 0, e);
    } else if (cleanedValue === "") {
      onChange?.(null, e);
    } else {
      onChange?.(numericValue, e);
    }
  };

  const handleBlur = () => {
    // On blur, ensure proper decimal formatting if needed
    if (displayValue && formatThousandSeparator) {
      const cleanedValue = displayValue.replace(/\./g, "").replace(",", ".");
      const formatted = formatNumberAsMoney(cleanedValue, "decimal");
      setDisplayValue(formatted);
    }
  };

  return (
    <TextField
      {...props}
      ref={ref}
      inputRef={inputRef}
      align={align}
      type="text"
      fieldType="number"
      inputMode={decimals ? "decimal" : "numeric"}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
